import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { useSiteData, defaultSettings, telHref } from "@/api";

const links = [
  { to: "/", label: "Accueil" },
  { to: "/services", label: "Services" },
  { to: "/realisations", label: "Réalisations" },
  { to: "/a-propos", label: "À propos" },
  { to: "/contact", label: "Contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const site = useSiteData({ chantiers: null, services: null, settings: defaultSettings });
  const telephone = site.settings?.telephone || defaultSettings.telephone;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const solid = scrolled || open;
  const onLight = solid || location.pathname !== "/";

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        solid
          ? "border-b border-ink/10 bg-cream/90 backdrop-blur-md"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 lg:px-12">
        <Link
          to="/"
          data-testid="nav-logo"
          className={`flex items-center gap-3 font-serif text-2xl tracking-tight transition-colors duration-500 ${onLight ? "text-ink" : "text-cream"}`}
        >
          <img src="/chantiers/logo-mark.png" alt="Logo Alexis Peinture" className="h-9 w-auto" />
          <span>
            Alexis <span className={`italic ${onLight ? "text-clay" : "text-sand"}`}>Peinture</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-9 lg:flex" data-testid="nav-desktop">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s|à/g, "-")}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors duration-300 ${
                  isActive
                    ? onLight
                      ? "text-clay"
                      : "text-sand"
                    : onLight
                      ? "text-bark hover:text-ink"
                      : "text-cream/85 hover:text-cream"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-cta-devis"
            className="rounded-full bg-clay px-6 py-2.5 text-sm font-medium text-cream transition-[background-color,transform] duration-300 hover:scale-[1.03] hover:bg-ink"
          >
            Demander un devis
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`transition-colors duration-500 lg:hidden ${onLight ? "text-ink" : "text-cream"}`}
          aria-label="Ouvrir le menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-ink/10 bg-cream lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s|à/g, "-")}`}
                  className={({ isActive }) =>
                    `py-3 font-serif text-2xl ${isActive ? "italic text-clay" : "text-ink"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <a
                href={telHref(telephone)}
                data-testid="nav-mobile-phone"
                className="mt-4 flex items-center gap-3 text-sm text-bark"
              >
                <Phone size={16} className="text-clay" /> {telephone}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
